/**
 * Event configuration service.
 * Fetches and parses event configuration from Commercetools category + product attributes.
 */
import { CATEGORIES_BY_ID, PRODUCT_DISCOUNTS } from '../../../shared/constants/api';
import * as api from '../../../shared/services/apiClient';
import type { EventConfiguration, ContentBlockItem, DiscountBannerData, GroupDiscountData, DiscountBlock, PolicyProduct } from '../types/home.types';

import { formatEventDates, getLocText } from './eventSchedule';

export const fetchEventConfiguration = async (categoryId: string): Promise<EventConfiguration> => {
    const res = await api.get(CATEGORIES_BY_ID(categoryId), {
        expand: ['custom.fields.configurationProduct'],
    });

    if (!res.ok) throw new Error(`Failed to fetch category: ${res.status}`);
    const category = await res.json();

    const configurationProduct = category.custom?.fields?.configurationProduct?.obj;
    if (!configurationProduct) {
        throw new Error('Configuration product not found');
    }

    const masterData = configurationProduct.masterData?.staged || configurationProduct.masterData?.current;
    const attributes: Array<{ name: string; value: unknown }> = masterData?.masterVariant?.attributes || [];
    const images: Array<{ url: string; label?: string }> = masterData?.masterVariant?.images || [];

    const logoImage = images.find((img) => img.label === 'event-logo');

    const venueVal = getLocText(category.custom?.fields?.venue);
    const locationVal = getLocText(category.custom?.fields?.location);
    const startDateVal = category.custom?.fields?.startDate as string | undefined;
    const endDateVal = category.custom?.fields?.endDate as string | undefined;

    const paymentMethodsConfig = category.custom?.fields?.allowedPaymentMethods?.key || category.custom?.fields?.allowedPaymentMethods || 'cardOnly';
    const invoiceMinThreshold = (category.custom?.fields?.invoiceMinimumAmount as number | null) ?? null;
    const invoiceCutoffDays = (category.custom?.fields?.invoiceCutoffDays as number) || 0;

    // -----------------------------------------------------------------------
    // Discount Banner
    // -----------------------------------------------------------------------
    let discountBanner: DiscountBannerData | null = null;
    const discountId = import.meta.env.VITE_DISCOUNT_ID;

    const discountBannerAttr = attributes.find((attr) => attr.name === 'discount-banner');
    let bannerText = { heading: '', description: '' };
    if (discountBannerAttr && Array.isArray(discountBannerAttr.value)) {
        const fields = discountBannerAttr.value as Array<{ name: string; value: unknown }>;
        bannerText = {
            heading: getLocText(fields.find((f) => f.name === 'heading')?.value),
            description: getLocText(fields.find((f) => f.name === 'description')?.value),
        };
    }

    if (discountId) {
        try {
            const discountRes = await api.get(PRODUCT_DISCOUNTS(discountId));
            if (!discountRes.ok) throw new Error(`Failed to fetch discount: ${discountRes.status}`);
            const discount = await discountRes.json();
            const now = new Date();
            const validFrom = discount.validFrom ? new Date(discount.validFrom) : null;
            const validUntil = discount.validUntil ? new Date(discount.validUntil) : null;
            const isValidTime = (!validFrom || now >= validFrom) && (!validUntil || now <= validUntil);

            if (discount.isActive && isValidTime) {
                discountBanner = {
                    heading: bannerText.heading || getLocText(discount.name) || 'Special Offer',
                    description: bannerText.description || getLocText(discount.description) || '',
                    countdown: discount.validUntil || null,
                };
            }
        } catch (err) {
            console.error('Failed to fetch discount banner:', err);
            // Non-fatal: proceed without discount banner
        }
    } else if (discountBannerAttr && Array.isArray(discountBannerAttr.value)) {
        const fields = discountBannerAttr.value as Array<{ name: string; value: unknown }>;
        const isActive = fields.find((f) => f.name === 'is-active')?.value;
        if (isActive) {
            discountBanner = {
                heading: bannerText.heading,
                description: bannerText.description,
                countdown: (fields.find((f) => f.name === 'countdown')?.value as string) || null,
            };
        }
    }


    // -----------------------------------------------------------------------
    // Content Blocks
    // -----------------------------------------------------------------------
    let contentBlocks: ContentBlockItem[] = [];
    const contentBlocksAttr = attributes.find((a) => a.name === 'contentBlocks');
    const buttonLabel = getLocText(attributes.find((a) => a.name === 'content-block-button-label')?.value) || 'Learn more';

    if (contentBlocksAttr && Array.isArray(contentBlocksAttr.value)) {
        contentBlocks = (contentBlocksAttr.value as Array<Array<{ name: string; value: unknown }>>).map((blockGroup) => ({
            title: getLocText(blockGroup.find((f) => f.name === 'contentBlockTitle')?.value),
            description: getLocText(blockGroup.find((f) => f.name === 'contentBlockDescription')?.value),
            linkUrl: '#',
        }));
    }

    // -----------------------------------------------------------------------
    // Group Discount
    // -----------------------------------------------------------------------
    const groupDiscountTitleAttr = attributes.find((a) => a.name === 'groupDiscountTitle');
    const groupDiscountDescAttr = attributes.find((a) => a.name === 'groupDiscountDescription');
    const groupDiscount: GroupDiscountData = {};
    if (groupDiscountTitleAttr || groupDiscountDescAttr) {
        groupDiscount.groupDiscountTitle = getLocText(groupDiscountTitleAttr?.value);
        groupDiscount.groupDiscountDescription = getLocText(groupDiscountDescAttr?.value);
    }

    // -----------------------------------------------------------------------
    // Discount Blocks (Accordion)
    // -----------------------------------------------------------------------
    let discountBlocks: DiscountBlock[] = [];
    const delegateDiscountTitleAttr = attributes.find((a) => a.name === 'delegateDiscountTitle');
    const delegateDiscountDescAttr = attributes.find((a) => a.name === 'delegateDiscountDescription');
    const delegateDiscountAccordionAttr = attributes.find((a) => a.name === 'delegateDiscountAccordion');

    if (delegateDiscountTitleAttr || delegateDiscountDescAttr || delegateDiscountAccordionAttr) {
        discountBlocks = [{
            delegateDiscountTitle: getLocText(delegateDiscountTitleAttr?.value),
            delegateDiscountDescription: getLocText(delegateDiscountDescAttr?.value),
            delegateDiscountAccordion: ((delegateDiscountAccordionAttr?.value as unknown[]) || []).map(getLocText),
        }];
    }

    // -----------------------------------------------------------------------
    // Promo Banner
    // -----------------------------------------------------------------------
    let promoBanner = null;
    const promoBannerAttr = attributes.find((a) => a.name === 'promoBanner');
    if (promoBannerAttr && Array.isArray(promoBannerAttr.value)) {
        const fields = promoBannerAttr.value as Array<{ name: string; value: unknown }>;
        const isActive =
            (fields.find((f) => f.name === 'isActive')?.value as { key?: string } | undefined)?.key === 'yes' ||
            fields.find((f) => f.name === 'isActive')?.value === 'yes';
        if (isActive) {
            promoBanner = { description: getLocText(fields.find((f) => f.name === 'promoDescription')?.value) };
        }
    }

    // -----------------------------------------------------------------------
    // Policy Product
    // -----------------------------------------------------------------------
    let policyProduct: PolicyProduct | null = null;
    const policyTitleAttr = attributes.find((a) => a.name === 'eventPolicyTitle');
    const policyDescAttr = attributes.find((a) => a.name === 'eventPolicyDescription');
    const eventPoliciesAttr = attributes.find((a) => a.name === 'eventPolicies');

    if (policyTitleAttr || policyDescAttr || eventPoliciesAttr) {
        let sections: Array<{ policyTitle: string; policyDescription: string }> = [];
        if (eventPoliciesAttr && Array.isArray(eventPoliciesAttr.value)) {
            sections = (eventPoliciesAttr.value as Array<Array<{ name: string; value: unknown }>>).map((sectionAttributes) => ({
                policyTitle: getLocText(sectionAttributes.find((attr) => attr.name === 'policyTitle')?.value),
                policyDescription: getLocText(sectionAttributes.find((attr) => attr.name === 'policyDescription')?.value),
            }));
        }
        policyProduct = {
            eventPolicyTitle: getLocText(policyTitleAttr?.value),
            eventPolicyDescription: getLocText(policyDescAttr?.value),
            eventPolicies: sections,
        };
    }

    return {
        logoUrl: logoImage?.url || null,
        venue: venueVal || '',
        location: locationVal || '',
        dates: formatEventDates(startDateVal ?? null, endDateVal ?? null),
        startDate: startDateVal,
        endDate: endDateVal,
        discountBanner,
        contentBlocks,
        contentBlockButtonLabel: buttonLabel,
        groupDiscount,
        discountBlocks,
        promoBanner,
        policyProduct,
        paymentMethodsConfig: paymentMethodsConfig,
        invoiceMinThreshold,
        invoiceCutoffDays,
        qrJourney: (category.custom?.fields?.qrJourney as { key?: string } | undefined)?.key || (category.custom?.fields?.qrJourney as string) || 'no',
        customerServiceContact: (category.custom?.fields?.customerServiceContact as string) || '',
        popularProductId: (category.custom?.fields?.popularProduct as { id?: string } | undefined)?.id || (category.custom?.fields?.popularProduct as string) || '',
        faqStatus: ((category.custom?.fields?.faqStatus as { key?: string } | undefined)?.key as 'enable' | 'disable' | undefined) || (category.custom?.fields?.faqStatus as 'enable' | 'disable' | undefined) || undefined,
        faqUrl: (category.custom?.fields?.faqUrl as string) || '',
    };
};
